import React from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faClock,
  faChartLine,
  faDatabase,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "./ActiveTriageSection.css";

const BUCKETS = [
  {
    key: "action",
    title: "Action Needed",
    icon: faExclamationTriangle,
    tone: "red",
    desc: "New or unresolved issues requiring prompt follow-up.",
    subKey: "action",
    href: "/care/triage?bucket=action",
  },
  {
    key: "monitor",
    title: "Monitor",
    icon: faClock,
    tone: "amber",
    desc: "Changes worth watching closely and reviewing soon.",
    subKey: "monitor",
    href: "/care/triage?bucket=monitor",
  },
  {
    key: "stable",
    title: "Stable / Improved",
    icon: faChartLine,
    tone: "green",
    desc: "Positive or stable changes with no urgent action needed.",
    subKey: "stable",
    hideAssigned: true,
    href: "/care/triage?bucket=stable",
  },
  {
    key: "reviewData",
    title: "Review Data",
    icon: faDatabase,
    tone: "gray",
    desc: "Missing, stale, conflicting, or insufficient information requiring review.",
    subKey: "reviewData",
    hideAssigned: true,
    href: "/care/triage?bucket=review",
  },
];

export default function ActiveTriageSection({
  summary,
  compact = false,
  overdueCount = 0,
}) {
  return (
    <section
      className={`care-triage-section${compact ? " care-triage-section--compact" : ""}`}
      aria-labelledby="care-active-triage-title"
    >
      <header className="care-triage-section__head">
        <h2 id="care-active-triage-title" className="care-triage-section__title">
          Active Triage
        </h2>
        <p className="care-triage-section__subtitle">
          New or unresolved changes detected from caregiver check-ins, nurse assessments,
          connected devices, and other care information.
        </p>
      </header>

      <div className="care-triage-circles">
        {BUCKETS.map(({ key, title, icon, tone, desc, subKey, hideAssigned, href }) => {
          const bucket = summary?.[subKey] || { total: 0, newCount: 0, assignedCount: 0 };
          const subline = hideAssigned
            ? null
            : `${bucket.newCount || 0} New · ${bucket.assignedCount || 0} Assigned`;

          return (
            <div key={key} className="care-triage-circle-col">
              <Link
                to={href}
                className={`care-triage-circle care-triage-circle--${tone}`}
                aria-label={`${title}, ${bucket.total ?? 0} cases`}
              >
                <span className="care-triage-circle__ring">
                  <span className="care-triage-circle__icon" aria-hidden="true">
                    <FontAwesomeIcon icon={icon} />
                  </span>
                  <span className="care-triage-circle__inner">
                    <span className="care-triage-circle__value">{bucket.total ?? 0}</span>
                    {subline ? (
                      <span className="care-triage-circle__meta">{subline}</span>
                    ) : null}
                  </span>
                </span>
                <span className="care-triage-circle__label">{title}</span>
              </Link>
              <p className="care-triage-circle__desc">{desc}</p>
            </div>
          );
        })}
      </div>

      <div className="care-triage-section__foot">
        <div className="care-triage-section__actions care-triage-section__actions--center">
          <Link to="/care/triage?bucket=all" className="care-triage-btn care-triage-btn--primary">
            See All Triage Cases →
          </Link>
          <Link to="/client-management" className="care-triage-btn care-triage-btn--ghost">
            All Clients
          </Link>
        </div>
        {overdueCount > 0 ? (
          <Link to="/care/triage?bucket=all&tab=overdue" className="care-triage-overdue">
            <FontAwesomeIcon icon={faExclamationTriangle} className="care-triage-overdue__icon" />
            <span className="care-triage-overdue__text">
              Overdue: {overdueCount} case{overdueCount === 1 ? " needs" : "s need"} follow-up
            </span>
            <FontAwesomeIcon icon={faChevronRight} className="care-triage-overdue__chev" />
          </Link>
        ) : null}
      </div>
    </section>
  );
}
